import type { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import type { Maybe } from '@/helpers/types';
import type { SnowplowContext } from '@/helpers/tracking/providers';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

/** Normal tracking property options + an optional trigger check method. */
export interface TrackingSpec extends TrackingProviderPropertiesInterface {
	triggerCheck?: (args: any) => boolean;
}

/**
 * Full record of the tracking specs for a feature, experiment, or whatever else.
 *
 * For type safety while writing the specs, include `satisfies TrackingSpecs` at the end.
 *
 * @example
 * ```ts
 * const specs = {
 * 	eventOne: {
 * 		action: 'event_one'
 * 		label: 'event'
 * 	}
 * 	eventTwo: {
 * 		action: 'event_two'
 * 		label: 'event'
 * 		triggerCheck: (providerId: number) => [350, 9, 119].includes(providerId)
 * 	}
 * } satisfies TrackingSpecs;
 * ```
 */
export type TrackingSpecs = Record<string, TrackingSpec>;

/** Returns a type-safe function to handle all the tracking for a feature as per the given specs. */
export function createSpecTracker<Specs extends TrackingSpecs>(
	/** Tracking specs for the feature. */
	specs: Specs,

	/** Tracking properties to include with every event, can be overwritten. */
	baseProperties: TrackingProviderPropertiesInterface = {},

	/** Contexts to include with every event. (Getter for reactivity). */
	baseContexts: () => Maybe<SnowplowContext>[] = () => []
) {
	type EventCallback<Event extends keyof Specs> = (data: Omit<Specs[Event], 'triggerCheck'>) => void;
	type EventCallbackMap<Specs extends TrackingSpecs> = {
		[Event in keyof Specs]?: Array<(data: Omit<Specs[Event], 'triggerCheck'>) => void>;
	};

	type TriggerCheckParams<Event extends keyof Specs> = Specs[Event] extends {
		triggerCheck?: (args: infer Params) => boolean;
	}
		? Params
		: null;

	const listeners: EventCallbackMap<Specs> = {};

	function sendEvent<Event extends keyof Specs>(
		event: Event,
		spec: Specs[Event],
		options?: TriggerCheckParams<Event>,
		contexts?: Maybe<SnowplowContext>[]
	) {
		const { triggerCheck, ...properties } = spec;

		const fullContexts = baseContexts()
			.concat(contexts)
			.filter(context => context != null) as SnowplowContext[];

		if (fullContexts.some(context => context == null)) return;
		if (triggerCheck != null && !triggerCheck(options)) return;

		const trackingProperties = { ...baseProperties, ...properties };

		listeners[event]?.forEach(cb => cb(trackingProperties));

		TrackingHelper.trackEvent('userinteraction', trackingProperties, fullContexts);
	}

	/** Send a tracking event using the event's defined spec. */
	function track<Event extends keyof Specs>(
		event: Event,
		// If there is triggerCheck then require the options while keeping contexts optional
		...args: Specs[Event] extends { triggerCheck?: (args: infer Params) => boolean }
			? [Params, Maybe<SnowplowContext>[]] | [Params]
			: [null, Maybe<SnowplowContext>[]?] | []
	) {
		sendEvent(event, specs[event], args[0] as TriggerCheckParams<Event>, args[1]);
	}

	/**
	 * Same as `track` but you can pass in overrides or dynamic values for the spec properties.
	 * Spec values you don't specify will be filled out from the spec.
	 *
	 * *Note* you can achieve the same thing with `track` and an event listener. This pattern is useful when you would rather keep all tracking logic contained in the same file as the spec.
	 *
	 * @example
	 * ```ts
	 * track.withSpec('trackingEvent', { action: `filter_${dynamicValue}` });
	 * ```
	 */
	track.withSpec = function <Event extends keyof Specs>(
		event: Event,
		spec: Partial<Specs[Event]>,
		triggerCheckParams?: TriggerCheckParams<Event>,
		contexts?: Maybe<SnowplowContext>[]
	) {
		sendEvent(event, { ...specs[event], ...spec }, triggerCheckParams, contexts);
	};

	/** Run a callback before the event is fired. Lets you modify the tracking specs. */
	track.on = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
		if (listeners[event] == null) {
			listeners[event] = [];
		}

		// Avoid adding the same callback multiple times
		if (listeners[event]!.some(callback => callback === cb)) return;

		listeners[event]!.push(cb);
	};

	/** Remove an event listener callback. */
	track.off = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
		if (listeners[event] == null) return;
		listeners[event] = listeners[event]!.filter(callback => callback !== cb);
	};

	return track;
}
